import {NgModule} from '@angular/core';
import {SM_ENTITY_PROVIDER} from './sm.entity-provider';
import {CoreModule} from '../../core/core.module';
import {SM_TEST_PROVIDERS} from './test/sm-test-definition';
import {SmGuidedSigningModule} from './sm-guided-signing.module';
import {SmSigningFlowComponent} from './component/sm-signing-flow/sm-signing-flow.component';
import {SmSigningPlaygroundPage} from './page/sm-signing-playground/sm-signing-playground.page';
import {
    SmSigningProcessOverviewPage
} from './page/overview/sm-signing-process-overview/sm-signing-process-overview.page';
import {SmSigningProcessObjectPage} from './page/object/sm-signing-process-object/sm-signing-process-object.page';
import {
    SmSignatureProfileObjectPage
} from './page/object/sm-signature-profile-object/sm-signature-profile-object.page';
import {
    SmSigningProcessObjectComponent
} from './component/object-components/sm-signing-process-object/sm-signing-process-object.component';
import {
    SmSignatureProfileObjectComponent
} from './component/object-components/sm-signature-profile-object/sm-signature-profile-object.component';
import {
    SmSigningProcessCrudComponent
} from './component/crud-object-component/sm-signing-process-crud/sm-signing-process-crud.component';
import {SmSignerCrudComponent} from './component/crud-object-component/sm-signer-crud/sm-signer-crud.component';
import {SmConfigPage} from './page/config/sm-config/sm-config.page';
import {SmSignatureTestPage} from './page/sm-signature-test/sm-signature-test.page';
import {SmAdHocFindingsComponent} from './component/sm-ad-hoc-findings/sm-ad-hoc-findings.component';
import {
    SmAdHocStampResultComponent
} from './component/sm-ad-hoc-stamp-result/sm-ad-hoc-stamp-result.component';
import {SmPlacementToolComponent} from './component/sm-placement-tool/sm-placement-tool.component';
import {
    SmChannelVariantSelectComponent
} from './component/sm-channel-variant-select/sm-channel-variant-select.component';
import {
    SmSignerAddressCaptureComponent
} from './component/sm-signer-address-capture/sm-signer-address-capture.component';
import {
    SmSignerLinkPanelComponent
} from './component/sm-signer-link-panel/sm-signer-link-panel.component';
import {
    SmInPersonIdentificationComponent
} from './component/sm-in-person-identification/sm-in-person-identification.component';
import {
    SmSignerCreateDialogComponent
} from './component/sm-signer-create-dialog/sm-signer-create-dialog.component';
import {
    SmExternalSigningPlaygroundComponent
} from './component/sm-external-signing-playground/sm-external-signing-playground.component';
import {
    SmSigningProcessTimelineComponent
} from './component/sm-signing-process-timeline/sm-signing-process-timeline.component';
import {SmSelectOverlayFitDirective} from './directive/sm-select-overlay-fit.directive';

@NgModule({
    declarations: [
        SmSigningFlowComponent,
        SmSigningPlaygroundPage,
        SmSigningProcessOverviewPage,
        SmSigningProcessObjectPage,
        SmSignatureProfileObjectPage,
        SmSigningProcessObjectComponent,
        SmSignatureProfileObjectComponent,
        SmSigningProcessCrudComponent,
        SmSignerCrudComponent,
        SmConfigPage,
        SmSignatureTestPage,
        SmAdHocFindingsComponent,
        SmAdHocStampResultComponent,
        // Declared HERE and not in SmGuidedSigningModule on purpose: the placement tool is
        // caseworker surface. The external signing application embeds the guided module and must
        // not inherit a tool that writes to an internal /mvsa endpoint.
        SmPlacementToolComponent,
        // Declared HERE and NOT in SmGuidedSigningModule, for the same reason: all three are
        // caseworker surface. The variant choice and the address capture are decisions taken BEFORE
        // the invitation goes out, and the link panel writes to internal /mvsa endpoints. The
        // external signing application embeds the guided module and must inherit none of them.
        SmChannelVariantSelectComponent,
        SmSignerAddressCaptureComponent,
        SmSignerLinkPanelComponent,
        // Caseworker surface as well, and for the sharpest version of the same reason: it writes the
        // identification act to an internal /mvsa endpoint and its whole premise is that a
        // CASEWORKER performed the identification. The external signing application, where nobody is
        // present, must never inherit it.
        SmInPersonIdentificationComponent,
        // Caseworker surface too. It writes a signer through an internal /mvsa endpoint and it exists
        // because the generic CRUD route registered for sm.SmSigningProcessSigner has no controller
        // behind it — the metadata form opened empty and no signer could be added from the interface
        // at all. The external signing application must not inherit it.
        SmSignerCreateDialogComponent,
        // Caseworker surface as well, and the plainest case of it: the bench of the sm playground
        // page. It writes to the playground endpoints, which exist only on the development profiles
        // and only for the admin role. The external signing application must not inherit it.
        SmExternalSigningPlaygroundComponent,
        // Caseworker surface, and declared HERE rather than in SmGuidedSigningModule for the
        // sharpest form of the reason the block above gives: the Verlauf reads the whole audit
        // backbone, every notification and every issued link of a signing process through QL. That
        // is an internal caseworker view of the evidence chain, and the external signing
        // application — where the reader is the SIGNING CUSTOMER — must not inherit a component
        // that can render another signer's delivery address.
        SmSigningProcessTimelineComponent,
        // Declared HERE and NOT in SmGuidedSigningModule, by the same rule as the components above:
        // it is applied to the two CASEWORKER selects — the channel variant and the identification
        // means — and to nothing the external signing application renders. A directive that only
        // constrains a width would do no harm there, but the module cut is a statement about which
        // surface a class belongs to, and this one belongs to the bench. @ticket TM-1000002003
        SmSelectOverlayFitDirective,
    ],
    exports: [
        SmSigningFlowComponent,
        SmSigningPlaygroundPage,
        SmSigningProcessOverviewPage,
        SmSigningProcessObjectPage,
        SmSignatureProfileObjectPage,
        SmSigningProcessObjectComponent,
        SmSignatureProfileObjectComponent,
        SmSigningProcessCrudComponent,
        SmSignerCrudComponent,
        SmConfigPage,
        SmSignatureTestPage,
        SmAdHocFindingsComponent,
        SmAdHocStampResultComponent,
        SmPlacementToolComponent,
        SmChannelVariantSelectComponent,
        SmSignerAddressCaptureComponent,
        SmSignerLinkPanelComponent,
        SmInPersonIdentificationComponent,
        SmSignerCreateDialogComponent,
        SmExternalSigningPlaygroundComponent,
        SmSigningProcessTimelineComponent,
        SmSelectOverlayFitDirective,
        // Re-exported, not only imported: the guided signing surface is embedded by cw and by the
        // sm test page, both of which reach it through SmModule. Dropping it from exports would
        // turn mvs-sm-signing-session and mvs-sm-signature-pad into unknown elements there.
        SmGuidedSigningModule,
    ],
    imports: [
        CoreModule,
        // The five signing components used to be declared here and therefore inherited the whole
        // CoreModule. They now live in their own module against their real dependencies, so a
        // second application can embed them without the internal shell behind them.
        SmGuidedSigningModule
    ],
    providers: [SM_ENTITY_PROVIDER, SM_TEST_PROVIDERS]
})
export class SmModule { }
